import { Box, Text } from "@vinomofo/components";

import * as React from "react";
import { SortOption } from "./types";
import { connectSortBy } from "react-instantsearch-core";
import selectArrow from "./selectArrow.svg";
import styled from "styled-components";
import { trackProductSortedByEvent } from "../../utils/event-tracking";
import { isUserAStaff } from "../../utils/loyalty";
import useAuth from "../../hooks/use-auth";
import { loyaltyTierNames } from "../../utils/constants";
import { useLoyalty } from "../../hooks/account/use-loyalty";

const StyledSelector = styled.select`
  color: ${({ theme }) => theme.colors.text.normal};
  padding-left: 4px;
  @media ${({ theme }) => theme.mq.min.desktop} {
    padding: 10px 30px 10px 10px;
  }
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  border: none;
  appearance: none;
  background: url(${selectArrow});
  background-repeat: no-repeat;
  background-size: 13px 7px;
  background-position: right 50%;
  cursor: pointer;
  flex-grow: 1;
  outline: 0;
  width: calc(100% - 50px);
  @media ${({ theme }) => theme.mq.max.mobile} {
    font-size: ${({ theme }) => theme.fontSizes[1]}px;
  }
`;

const StyledText = styled(Text)`
  white-space: nowrap;
`;

interface SortSelectorProps {
  items: Array<SortOption>;
  currentRefinement: string;
  refine: (value: string) => void;
  createURL: () => void;
  defaultRefinement?: string;
}

export const SORT_OPTIONS: Array<SortOption> = [
  {
    value: `popular`,
    index: `${process.env.GATSBY_ALGOLIA_OFFERS_INDEX}`,
    label: "Popular",
  },
  {
    value: `price_asc`,
    index: `${process.env.GATSBY_ALGOLIA_OFFERS_INDEX}_price_asc`,
    label: "Price – Low to High",
  },
  {
    value: `price_desc`,
    index: `${process.env.GATSBY_ALGOLIA_OFFERS_INDEX}_price_desc`,
    label: "Price – High to Low",
  },
];

export const SortSelector = connectSortBy(
  ({
    currentRefinement,
    refine,
    items = SORT_OPTIONS,
    ...rest
  }: SortSelectorProps): JSX.Element => {
    const currentSelected = SORT_OPTIONS.find(
      (i) => i.index === currentRefinement
    );
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      e.preventDefault();
      // Find the selected sort option
      const selected = SORT_OPTIONS.find((i) => i.value === e.target.value);
      if (!selected) return;

      if (selected) {
        trackProductSortedByEvent("Sorted By", selected.label);
      }

      localStorage.setItem("algoliaIndexName", selected.index);

      refine(selected.value);
    };

    return (
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={[12, 15, 20]}
        borderBottom={["1px solid", "1px solid", "1px solid"]}
        borderTop={["1px solid", "1px solid", "0"]}
        borderRight={["1px solid", "1px solid", "0"]}
        borderLeft={["1px solid", "1px solid", "0"]}
        borderColor={["divider", "divider", "divider"]}
        borderRadius={["6px 6px 0 0", "6px 6px 0 0", "0"]}
        {...rest}
      >
        <StyledText fontSize={[1, 2, 2]} fontWeight="500" color="button">
          Sort by:{" "}
        </StyledText>

        <StyledSelector value={currentSelected?.value} onChange={handleChange}>
          {items.map((option, i) => (
            <option key={i} value={option.value}>
              {option.label}
            </option>
          ))}
        </StyledSelector>
      </Box>
    );
  }
);
